// Centralised content for RSR Australia
// Image URLs verified — Unsplash with stable photo IDs.

const UNS = (id, w = 1400) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=75`;
const PEX = (id, w = 1400) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}`;

export const HERO_IMAGE = PEX(8853502); // Pexels: tradesperson with hard hat installing solar panels
export const ABOUT_IMAGE = UNS("1581094288338-2314dddb7ece"); // architect/plans on desk
export const OFFICE_IMAGE = UNS("1497366216548-37526070297c"); // modern office hallway
export const HOME_INTERIOR_IMAGE = UNS("1493809842364-78817add7ffb"); // cosy home interior
export const TOOLBELT_IMAGE = UNS("1542621334-a254cf47733d"); // tools / hardware close-up

export const SERVICES = [
  {
    slug: "renovations",
    name: "Renovations",
    short: "Kitchens, bathrooms, full home refreshes — finished sharp.",
    long: "From a tired kitchen pull-out to a full home transformation, we project-manage every trade so you get one schedule, one budget and one team that owns the result.",
    bullets: [
      "Full kitchen, bathroom and laundry renovations",
      "Whole-home refits and floor plan reworks",
      "Single point of contact across all trades",
      "Fixed quotes and clear timelines",
    ],
    image: UNS("1581858726788-75bc0f6a952d"), // kitchen renovation
    accent: "01",
  },
  {
    slug: "cleaning",
    name: "Commercial & Residential Cleaning",
    short: "Routine, deep, and end-of-lease cleans done by people who actually care.",
    long: "Recurring office and strata cleaning, deep cleans, end-of-lease and post-construction make-goods. Insured, vetted teams and a finish you can hand straight back.",
    bullets: ["Office and strata recurring cleans", "Deep & end-of-lease cleans", "Post-build make-goods", "Window and high-reach cleaning"],
    image: UNS("1581578731548-c64695cc6952"), // cleaning supplies
    accent: "02",
  },
  {
    slug: "maintenance",
    name: "Residential & Commercial Maintenance",
    short: "Scheduled and reactive maintenance for properties of every shape.",
    long: "Planned preventative maintenance and rapid reactive callouts for landlords, agents, body corps and homeowners. One number, every trade covered.",
    bullets: ["Preventative maintenance schedules", "Reactive callouts and small repairs", "Property manager reporting", "Strata & multi-site programs"],
    image: PEX(8853502), // Pexels: tradesperson with hard hat
    accent: "03",
  },
  {
    slug: "cctv-antennas-cabling",
    name: "CCTV, Antennas & Cabling",
    short: "See more, signal stronger, wired neat.",
    long: "Domestic and commercial CCTV install and service, TV antenna repairs and structured data cabling — clean cable runs, sensible camera placement, real footage when you need it.",
    bullets: ["CCTV install & service", "TV antennas and signal boosters", "Data & network cabling", "Existing system upgrades"],
    image: UNS("1557597774-9d273605dfa9"), // CCTV camera
    accent: "04",
  },
  {
    slug: "roofing",
    name: "Roofing Repairs & Installations",
    short: "Leak-free, weather-tight, made to last.",
    long: "Roof restorations, tile and metal repairs, gutter and downpipe replacements, and full re-roofs. Strong on weatherproofing, neat on detail.",
    bullets: ["Leak detection & repair", "Tile and Colorbond re-roofs", "Gutters, downpipes & flashing", "Roof restorations and recoats"],
    image: UNS("1605276374104-dee2a0ed3cd6"), // residential house with prominent roof
    accent: "05",
  },
  {
    slug: "handyman",
    name: "Handyman & Specialised Solutions",
    short: "The odd jobs nobody else wants to schedule.",
    long: "Door adjustments, shelving, TV mounts, flat-pack assembly, picture hanging, fixture swaps — the small jobs that have been on your list for months.",
    bullets: ["Door & lock adjustments", "Shelving, TV mounts, flat-pack", "Tap, fitting & fixture swaps", "General home fixes"],
    image: UNS("1581094288338-2314dddb7ece"), // gloves and tools
    accent: "06",
  },
  {
    slug: "carpentry",
    name: "Carpentry",
    short: "Built-ins, decks, framing and fine joinery.",
    long: "Custom built-in storage, decks and pergolas, internal trim, framing and repairs. Sharp lines, square corners, real timber craft.",
    bullets: ["Built-in wardrobes & joinery", "Decks, pergolas & outdoor", "Framing, trim & cornices", "Door hanging & repairs"],
    image: UNS("1504148455328-c376907d081c"), // wood deck / timber
    accent: "07",
  },
  {
    slug: "electrical",
    name: "Electrical Work",
    short: "Licensed sparkies for safe, code-compliant work.",
    long: "Power points, lighting, switchboards, smoke alarms, EV chargers and fault-finding. Fully licensed, fully insured, no shortcuts on safety.",
    bullets: ["Power & lighting installs", "Switchboard upgrades", "Smoke alarms & safety", "EV charger installation"],
    image: UNS("1621905251918-48416bd8575a"), // electrical wiring / hand
    accent: "08",
  },
  {
    slug: "general-repairs",
    name: "General Repairs",
    short: "Anything broken, fixed properly the first time.",
    long: "Patch and paint, broken tiles, loose hinges, dodgy taps, squeaky floorboards. We fix the small stuff so the small stuff doesn't become the big stuff.",
    bullets: ["Drywall patching & paint touch-up", "Tile, grout & sealant repairs", "Hinges, latches & hardware", "Squeaks, leaks & sticks"],
    image: UNS("1607400201515-c2c41c07d307"), // drill on wood
    accent: "09",
  },
  {
    slug: "landscaping",
    name: "Landscaping",
    short: "Lawns, gardens, paving and structures — sorted.",
    long: "Garden refreshes, lawn install and maintenance, paving, retaining walls and outdoor structures. Practical landscaping that lifts the whole property.",
    bullets: ["Garden design & refresh", "Lawn install & maintenance", "Paving & retaining walls", "Outdoor structures"],
    image: UNS("1416879595882-3373a0480b5b"), // garden / lawn
    accent: "10",
  },
  {
    slug: "locksmithing",
    name: "Locksmithing",
    short: "Rekeys, new locks, lockouts — fast.",
    long: "Lock rekeys, new lock installs, deadbolt upgrades and emergency lockouts. We get you back inside and your place properly secured.",
    bullets: ["Rekeys & master keying", "Deadbolt upgrades", "Emergency lockouts", "Door hardware swaps"],
    image: UNS("1582139329536-e7284fece509"), // keys
    accent: "11",
  },
  {
    slug: "painting",
    name: "Painting",
    short: "Interior, exterior, commercial — clean lines, no drips.",
    long: "Interior and exterior repaints, feature walls, commercial fit-outs and trim work. Proper prep, premium paint, sharp cut-in.",
    bullets: ["Interior & exterior repaints", "Feature walls & ceilings", "Commercial fit-outs", "Trim & cabinet refresh"],
    image: UNS("1562259949-e8e7689d7828"), // paint roller / painting
    accent: "12",
  },
  {
    slug: "plumbing",
    name: "Plumbing",
    short: "Leaks, blockages, hot water and full fit-outs.",
    long: "Licensed plumbers for leaks, blockages, tap and toilet repairs, hot water systems and bathroom fit-outs. On time, properly priced.",
    bullets: ["Leaks & blockages", "Hot water systems", "Tap & toilet repair/install", "Bathroom fit-out plumbing"],
    image: UNS("1581244277943-fe4a9c777189"), // plumbing pipes
    accent: "13",
  },
  {
    slug: "pool-services",
    name: "Pool Services",
    short: "Clean water, healthy chemistry, gear that runs.",
    long: "Regular pool service, equipment install and repair, water balancing and seasonal openings/closings. So the pool's ready when the family is.",
    bullets: ["Regular cleaning & maintenance", "Pump & filter install / repair", "Water balancing & chemistry", "Seasonal opening / closing"],
    image: UNS("1576013551627-0cc20b96c2a7"), // pool
    accent: "14",
  },
];

export const SERVICE_BY_SLUG = Object.fromEntries(SERVICES.map(s => [s.slug, s]));

export const PROJECT_GALLERY = [
  { title: "Roof Restoration", location: "Sydney", category: "Roofing", image: UNS("1605276374104-dee2a0ed3cd6"), desc: "Full pressure-clean, rebed and repoint on a 1960s tile roof. Re-coated to match the original profile." },
  { title: "Bathroom Renovation", location: "Newtown", category: "Renovations", image: UNS("1552321554-5fefe8c9ef14"), desc: "Stripped to brick. New plumbing, waterproofing, tiles and vanity. Two-week turnaround." },
  { title: "Commercial Repaint", location: "Parramatta", category: "Painting", image: UNS("1562259949-e8e7689d7828"), desc: "After-hours repaint of a 600m² retail tenancy with zero downtime to the business." },
  { title: "Backyard Deck Build", location: "Wollongong", category: "Carpentry", image: UNS("1504148455328-c376907d081c"), desc: "Spotted-gum deck on steel frame, with integrated bench seating and stair down to the lawn." },
  { title: "Kitchen Refit", location: "Bondi", category: "Renovations", image: UNS("1556909114-f6e7ad7d3136"), desc: "Replaced the layout, opened to the dining room, new stone tops and pantry." },
  { title: "CCTV System Install", location: "Chatswood", category: "Security", image: UNS("1557597774-9d273605dfa9"), desc: "Eight-camera 4K system with cloud backup and discreet cable runs through the cavity." },
  { title: "Garden & Paving", location: "Manly", category: "Landscaping", image: UNS("1416879595882-3373a0480b5b"), desc: "Native garden refresh with sandstone paving and a new irrigation line." },
  { title: "Switchboard Upgrade", location: "Hornsby", category: "Electrical", image: UNS("1621905251918-48416bd8575a"), desc: "Replaced an ageing fuse board with a fully RCD-protected switchboard. Same-day swap." },
  { title: "Pool Equipment Swap", location: "Cronulla", category: "Pool", image: UNS("1576013551627-0cc20b96c2a7"), desc: "New variable-speed pump and chlorinator. Cleaner water, lower electricity." },
  { title: "Office Deep Clean", location: "North Sydney", category: "Cleaning", image: UNS("1581578731548-c64695cc6952"), desc: "Quarterly deep clean across 1,200m² of office. Carpets, glass, all touch-points." },
];

export const TESTIMONIALS = [
  { quote: "RSR sorted three trades in a week we couldn't book separately for a month. Roof patched, ceiling re-painted, new lights — done.", name: "Emma R.", role: "Homeowner, Sydney" },
  { quote: "We use them across 14 retail sites. Reactive jobs get sorted same-week and the reporting is honest.", name: "Daniel K.", role: "Facilities Lead, Retail Group" },
  { quote: "Renovated our ensuite end-to-end. One quote, one schedule, no chasing. Pricing held.", name: "Priya S.", role: "Homeowner, Newcastle" },
  { quote: "Fast, polite, properly licensed. Don't think twice — just book them.", name: "Marcus T.", role: "Property Manager" },
  { quote: "I'd been putting off a list of small fixes for a year. Knocked them all out in a day.", name: "Hannah W.", role: "Homeowner, Brisbane" },
  { quote: "Honest quote, honest invoice. That's harder to find than it should be.", name: "James O.", role: "Strata Manager" },
];

export const FAQS = [
  { q: "Are you insured and licensed?", a: "Yes. All our trades hold the relevant Australian licences and we carry full public liability and workers' compensation insurance. Happy to provide certificates on request." },
  { q: "What areas do you service?", a: "We operate across major metro and regional centres in Australia — Sydney, Melbourne, Brisbane, Gold Coast, Newcastle, Wollongong, Canberra, Adelaide and Perth. Tell us the suburb when you enquire; if we don't service it, we'll point you to someone trusted who does." },
  { q: "How do I get a quote?", a: "Call 0410 850 550, email hello@rsraustralia.com.au, or send the form on our contact page. For most jobs we'll quote within one business day; larger jobs may include a free on-site walkthrough." },
  { q: "How fast can you respond?", a: "Urgent reactive jobs are usually attended within 24–48 hours, often same-day where possible. Standard jobs are scheduled within the week." },
  { q: "Do you work on both residential and commercial properties?", a: "Yes — homeowners, landlords, body corporates, property managers, retail and commercial sites. We scale the crew to match the job." },
  { q: "Do you provide written quotes?", a: "Always. Every job gets a clear written quote with scope, inclusions and timeline before any work starts." },
  { q: "Do you guarantee your work?", a: "Yes. All workmanship is backed by our written guarantee. If anything isn't right after we leave, we come back and put it right — no fuss." },
  { q: "Can you coordinate multiple trades for one project?", a: "That's our specialty. One coordinator owns the schedule, communication and quality across every trade on the job, so you only ever ring one number." },
];

export const TRUST_POINTS = [
  "Fully Insured", "Licensed Trades", "7-Day Availability", "Free Quotes",
  "Residential & Commercial", "Australia-Wide Coverage", "Workmanship Guarantee", "Written Quotes",
];

export const SERVICE_AREAS = [
  "Sydney", "Melbourne", "Brisbane", "Gold Coast", "Newcastle",
  "Wollongong", "Canberra", "Adelaide", "Perth", "Central Coast",
  "Sunshine Coast", "Geelong",
];

export const PROCESS_STEPS = [
  { n: "01", title: "Tell us the job", body: "Call, email or send the form — a couple of photos and a suburb is plenty to get started." },
  { n: "02", title: "Get a written quote", body: "Most quotes come back inside one business day. Larger jobs include a free on-site walkthrough." },
  { n: "03", title: "We schedule the crew", body: "We book a time that suits, confirm the day before, and turn up on time with the right trades." },
  { n: "04", title: "Walk-through & done", body: "We clean up, walk you through the work, and stand behind it with a written workmanship guarantee." },
];

export const STATS = [
  { k: "14", v: "Trade specialities" },
  { k: "2,400+", v: "Jobs completed" },
  { k: "97%", v: "Repeat-client rate" },
  { k: "24–48h", v: "Reactive response" },
];

export const PHONE_DISPLAY = "0410 850 550";
export const PHONE_TEL = "+61410850550";
export const EMAIL = "hello@rsraustralia.com.au";
